import { AgGridAngular } from "@ag-grid-community/angular";
import {
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  ColumnRowGroupChangedEvent,
  GetRowIdParams,
  GridOptions,
  ICellRendererParams,
  IRowNode,
  ITooltipParams,
  KeyCreatorParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "@ag-grid-community/core";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { AccountType, Currency, InventoryConfig, Strategy } from "@front/m19-api-client";
import { getFilteredLeafNodes, gridConfig, PARENT_ASIN_COL_NAME } from "@front/m19-grid-config";
import { Metric } from "@front/m19-metrics";
import { AdStatsEx, CurrencyStat, OrderStats } from "@front/m19-models";
import {
  AccountSelectionService,
  AsinService,
  AuthService,
  CurrencyService,
  MetricsSelectorPage,
  MetricsSelectorKey,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import {
  ActionButtonsComponent,
  IButtonDirective,
  IGridBadgeComponent,
  ITooltipDirective,
  ModalOptions,
  ModalService,
  SpinnerComponent,
} from "@front/m19-ui";
import { mergeAllStats, Utils } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { actionColumnProperties, currencyColumn } from "@m19-board/grid-config/grid-columns";
import {
  ACTIONS_COL_ID,
  exportGridCsv,
  getCsvFileName,
  IMAGE_COL_ID,
  restoreDefaultColumns,
} from "@m19-board/grid-config/grid-config";
import { GridConfigService } from "@m19-board/grid-config/grid-config.service";
import { AdType } from "@m19-board/shared/compact-filter/compact-filter.component";
import { NET_PURE_PRODUCT_MARGIN } from "@m19-board/models/MetricsDef";
import { AsinLinkComponent } from "@m19-board/product-view/asin-link.component";
import {
  ProfitDetailsComponent,
  ProfitDetailsModalData,
} from "@m19-board/profit-board/profit-details/profit-details.component";
import {
  ChartData,
  ChartModalComponent,
  ChartModalInputData,
} from "@m19-board/shared/chart-modal/chart-modal.component";
import { AdStats } from "libs/m19-services/src/lib/api-client/models/AdStats";
import { combineLatest, map, Observable, of, shareReplay, switchMap } from "rxjs";
import {
  convertToAdStats,
  GLOBAL_CHART_METRICS_SELLER,
  GLOBAL_CHART_METRICS_VENDOR,
} from "./product-analysis.component";
import { ProductDetailsComponent } from "./product-details/product-details.component";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { GridLayoutComponent } from "@m19-board/containers/grid-layout/grid-layout.component";
import { ProductThumbnailGridComponent } from "@m19-board/product-view/product-thumbnail-grid.component";
import { DefaultAdStatsMetricsWithSameScale } from "@front/m19-models/dataset/Dataset";

export interface ProductAnalysisGridData extends AdStats {
  brand?: string;
  parentAsin?: string;
  productTitle?: string;
  productTag1?: string;
  productTag2?: string;
  previousData?: AdStats;
  activeStrategies?: Strategy[];
}

@Component({
  selector: "app-product-analysis-grid",
  template: `<app-grid-layout *transloco="let t">
    <div class="relative flex w-full flex-col gap-2">
      @if (loading()) {
        <div class="absolute z-10 flex h-full w-full items-center justify-center bg-white opacity-70">
          <ISpinner [display]="true" />
        </div>
      }
      <div class="flex justify-between">
        <button iButton variant="secondary" (click)="toggleParentAsin()">
          {{ isGroupByParent() ? t("product-tracker.split_by_child_asin") : t("product-tracker.group_by_parent_asin") }}
        </button>
        <div class="flex gap-2">
          <button
            iButton
            variant="secondary"
            [iTooltip]="t('common.restore_columns')"
            (click)="restoreDefaultColumns()"
          >
            {{ t("common.restore_columns") }}
          </button>
          <app-export-button tooltipValue="{{ t('common.export_as_csv') }}" (export)="exportGridCsv()" />
        </div>
      </div>
      <ag-grid-angular
        class="ag-theme-quartz h-[45rem] w-full"
        #grid
        [columnDefs]="columnDefs()"
        [rowData]="gridData()"
        [gridOptions]="gridOptions()"
        [animateRows]="true"
        (columnRowGroupChanged)="onColumnRowGroupChanged($event)"
      />
    </div>
  </app-grid-layout>`,
  imports: [
    ExportButtonComponent,
    AgGridAngular,
    GridLayoutComponent,
    SpinnerComponent,
    IButtonDirective,
    ITooltipDirective,
    TranslocoDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductAnalysisGridComponent {
  private readonly gridConfigService = inject(GridConfigService);
  private readonly translocoService = inject(TranslocoService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly authService = inject(AuthService);
  private readonly asinService = inject(AsinService);
  private readonly statsService = inject(StatsService);
  private readonly currencyService = inject(CurrencyService);
  private readonly destroyRef = inject(DestroyRef);

  readonly DETAIL_ASIN_METRICS_SELLER = GLOBAL_CHART_METRICS_SELLER;
  readonly DETAIL_ASIN_METRICS_VENDOR = [...GLOBAL_CHART_METRICS_VENDOR, NET_PURE_PRODUCT_MARGIN];

  readonly gridData = input<ProductAnalysisGridData[]>([]);
  readonly loading = input<boolean>(true);
  readonly selectedMetrics = input.required<Metric<CurrencyStat>[]>();
  readonly selectedAdTypes = input.required<AdType[]>();

  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  readonly currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  readonly locale = toSignal(this.authService.loggedUser$.pipe(map((user) => user.locale)), { requireSync: true });

  readonly inventoryConfig = toSignal<InventoryConfig>(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.asinService.getInventoryConfig(am.accountId, am.marketplace)),
    ),
  );
  readonly localStorageKey: MetricsSelectorKey = { page: MetricsSelectorPage.salesAndAdvertising };
  private readonly localStorageKeyGridPrefix = this.localStorageKey.page + "_grid_";
  private readonly localStorageKeyGrid = computed(
    () => this.localStorageKeyGridPrefix + this.accountMarketplace()?.accountType,
  );
  readonly detailAsinMetrics = computed(() => this.getDetailAsinMetrics(this.accountMarketplace()?.accountType));
  readonly isGroupByParent = signal<boolean>(false);

  readonly grid = viewChild<AgGridAngular>("grid");

  readonly gridOptions = computed<GridOptions<ProductAnalysisGridData>>(() => {
    const commonOptions = gridConfig({
      gridKey: this.localStorageKeyGrid(),
      withFooterRow: true,
      aggFunction: mergeAllStats,
    });
    return {
      ...commonOptions,
      pagination: true,
      paginationPageSize: 10,
      paginationPageSizeSelector: [10, 25, 50, 100],
      rowGroupPanelShow: "always",
      context: { componentParent: this },
      autoGroupColumnDef: { pinned: "left" },
      showOpenedGroup: false,
      enableCharts: true,
      groupDisplayType: "singleColumn",
      rowDragManaged: false,
      debounceVerticalScrollbar: true, // see https://www.ag-grid.com/angular-data-grid/scrolling-performance/#debounce-vertical-scroll
      suppressRowHoverHighlight: true,
      getRowId: (params: GetRowIdParams<AdStats>) => params.data.asin ?? "",
      getGroupRowAgg: (params) => {
        const targetedNodes: IRowNode<ProductAnalysisGridData>[] = [];

        params.nodes.forEach((n) => {
          getFilteredLeafNodes(n).forEach((c: IRowNode<ProductAnalysisGridData>) => {
            targetedNodes.push(c);
          });
        });

        return targetedNodes.reduce(
          (acc, curr) => {
            const sIds = new Set(acc.activeStrategies!.map((st) => st.strategyId));
            (curr.data?.activeStrategies ?? []).forEach((st) => {
              if (!sIds.has(st.strategyId)) {
                sIds.add(st.strategyId);
                acc.activeStrategies!.push(st);
              }
            });
            if (curr.data?.previousData) {
              acc.previousData = acc.previousData
                ? mergeAllStats(
                    acc.previousData as OrderStats & AdStatsEx,
                    curr.data.previousData as OrderStats & AdStatsEx,
                  )
                : curr.data.previousData;
            }
            return mergeAllStats(acc as OrderStats & AdStatsEx, curr.data! as OrderStats & AdStatsEx);
          },
          { activeStrategies: [] } as ProductAnalysisGridData,
        );
      },
    };
  });

  private readonly productInfoColumnDefs = {
    headerName: this.translocoService.translate("sales-advertising.product_info"),
    marryChildren: true, // prevent column group break
    children: [
      {
        colId: IMAGE_COL_ID,
        pinned: "left",
        field: "asin",
        filter: false,
        sortable: false,
        cellClass: "sensitive-data",
        headerName: this.translocoService.translate("catalog-page.image"),
        width: 90,
        cellRendererSelector: (params: ICellRendererParams<ProductAnalysisGridData, string>) => {
          if (!params.value) return undefined;
          else {
            return {
              component: ProductThumbnailGridComponent,
              params: {
                asin: params.value,
                smallImg: true,
                marketplace: this.accountMarketplace().marketplace,
                customSizeClass: "size-16",
              },
            };
          }
        },
      },
      {
        headerName: this.translocoService.translate("common.product"),
        pinned: "left",
        colId: "asin_col",
        field: "asin",
        floatingFilter: true,
        filter: "agTextColumnFilter",
        cellRendererSelector: (params: ICellRendererParams<ProductAnalysisGridData, string>) => {
          if (params.node.group) return undefined;
          return {
            component: AsinLinkComponent,
            params: {
              asin: params.value,
              marketplace: this.accountMarketplace().marketplace,
            },
          };
        },
      },
      {
        headerName: this.translocoService.translate("common.title"),
        floatingFilter: true,
        filter: "agTextColumnFilter",
        field: "productTitle",
        pinned: "left",
        width: 300,
        tooltipValueGetter: (p: ITooltipParams<ProductAnalysisGridData, string>) => p.value,
        cellClass: "sensitive-data",
      },
      {
        chartDataType: "excluded",
        colId: "stratCol",
        headerName: this.translocoService.translate("product-details.active_strategies"),
        headerTooltip: this.translocoService.translate("product-details.active_strategies"),
        enableRowGroup: true,
        filter: "agNumberColumnFilter",
        pinned: "left",
        valueGetter: (params: ValueGetterParams<ProductAnalysisGridData>) => {
          if (params.node?.isRowPinned()) {
            return undefined;
          }
          return (
            (params.node?.group
              ? params.node.aggData.activeStrategies?.length
              : params.data?.activeStrategies?.length) ?? 0
          );
        },
        cellRendererSelector: (params: ICellRendererParams<ProductAnalysisGridData, number>) => {
          if (params.value === undefined) return undefined;
          return {
            component: IGridBadgeComponent,
            params: {
              label: (params.value ?? 0) + "",
              rounded: true,
            },
          };
        },
        cellClass: "text-center cursor-pointer",
        onCellClicked: (e: CellClickedEvent<ProductAnalysisGridData>) => {
          const modalOptions = {
            modalTitle: this.translocoService.translate("product-details.active_strategies"),
            data: {
              strategies: (e.node?.group ? e.node.aggData.activeStrategies : e.data?.activeStrategies) ?? [],
              asinnumber: e.data?.asin ?? "",
              marketplace: this.accountMarketplace().marketplace,
            },
          };
          this.modalService.openModal(ProductDetailsComponent, modalOptions);
        },
      },
      {
        field: "brand",
        headerValueGetter: () => this.translocoService.translate("common.brand"),
        floatingFilter: true,
        hide: true,
        enableRowGroup: true,
        keyCreator: (params: KeyCreatorParams<ProductAnalysisGridData>) => {
          return params.value?.toLocaleLowerCase(); // this is to group by brand regardless of case
        },
        cellRendererSelector: (params: ICellRendererParams<ProductAnalysisGridData, string>) => {
          if (params.node.isRowPinned() || !params.value) return undefined;
          return {
            component: IGridBadgeComponent,
            params: {
              label: params.value,
              color: "gray",
            },
          };
        },
        cellClass: "sensitive-data",
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: (params: ValueFormatterParams<ProductAnalysisGridData, string>) => params.value,
        },
      },

      {
        colId: "ProductTag1",
        field: "productTag1",
        headerValueGetter: () =>
          this.inventoryConfig()?.customField1Name
            ? Utils.titleCase(this.inventoryConfig()?.customField1Name)!
            : this.translocoService.translate("custom-field-edition.custom_field_1")!,
        pinned: "left",
        hide: true,
        floatingFilter: true,
        filter: "agSetColumnFilter",
        enableRowGroup: true,
      },
      {
        colId: "ProductTag2",
        field: "productTag2",
        headerValueGetter: () =>
          this.inventoryConfig()?.customField2Name
            ? Utils.titleCase(this.inventoryConfig()?.customField2Name)!
            : this.translocoService.translate("custom-field-edition.custom_field_2")!,
        pinned: "left",
        hide: true,
        floatingFilter: true,
        filter: "agSetColumnFilter",
        enableRowGroup: true,
      },
      {
        field: "parentAsin",
        colId: PARENT_ASIN_COL_NAME,
        headerName: this.translocoService.translate("product360.parent_asin"),
        hide: true,
        pinned: "left",
        enableRowGroup: true,
        cellClass: "sensitive-data",
        floatingFilter: true,
        filter: "agTextColumnFilter",
      },
    ],
  };

  private readonly actionColumn: ColDef<ProductAnalysisGridData, string> = {
    ...actionColumnProperties<ProductAnalysisGridData, string>(),
    cellRenderer: ActionButtonsComponent,
    cellRendererParams: (params: ICellRendererParams<ProductAnalysisGridData, string>) => {
      const actionButtons = [
        {
          icon: "chartLine",
          tooltipValue: `Display ${params.node.isRowPinned() ? "total" : ""} ASIN chart`,
          color: "white",
          onClick: () => {
            this.openAsinChartModal(params.node);
          },
        },
      ];
      if (this.accountMarketplace().accountType !== AccountType.VENDOR) {
        actionButtons.push({
          color: "white",
          icon: "list",
          tooltipValue: "Show Profit Details",
          onClick: () => {
            this.modalService.openModal<ProfitDetailsModalData, void>(ProfitDetailsComponent, {
              size: "md",
              modalTitle: "Profit Details",
              data: {
                asin: params.node.data?.asin,
                marketplace: this.accountMarketplace().marketplace,
                data: params.node.group ? params.node.aggData : params.node.data,
                locale: this.locale(),
                currency: this.currency(),
              },
            });
          },
        });
      }
      return { actionButtons };
    },
  };

  // currency column is used for CSV export
  private readonly currencyColumn: ColDef<ProductAnalysisGridData, Currency> = currencyColumn<ProductAnalysisGridData>(
    () => this.currency(),
  );

  readonly columnDefs = computed(() => {
    const metricsColDefs: ColGroupDef<ProductAnalysisGridData>[] =
      this.gridConfigService.getMetricsColDef<ProductAnalysisGridData>(this.detailAsinMetrics(), true, (data) => ({
        locale: this.locale(),
        currency: this.currency(),
        previousData: data.previousData,
      }));
    return [this.productInfoColumnDefs, ...metricsColDefs, this.actionColumn, this.currencyColumn];
  });

  constructor() {
    toObservable(this.gridData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // need to update grid data this way to ensure proper refresh of metric evolution cells
        const grid = this.grid();
        if (!grid) return;
        grid.api.updateGridOptions({ rowData: this.gridData() });
        grid.api.refreshCells({ force: true });
      });
  }

  exportGridCsv(): void {
    const accountMarketplace = this.accountMarketplace();
    const fileName = getCsvFileName(
      "product_analysis_",
      accountMarketplace.accountGroupName,
      accountMarketplace.marketplace,
      this.userSelectionService.getDateRangeStr(),
    );
    const columnKeys: string[] = this.grid()!
      .api!.getAllDisplayedColumns()
      .map((c) => c.getColId())
      .filter((c) => c !== IMAGE_COL_ID && !c.startsWith(ACTIONS_COL_ID))
      .concat(["currency"]);
    // set customField header name
    this.grid()!.api.getColumnDef("ProductTag1")!.headerName =
      this.inventoryConfig()?.customField1Name ?? "ProductTag1";
    this.grid()!.api.getColumnDef("ProductTag2")!.headerName =
      this.inventoryConfig()?.customField2Name ?? "ProductTag2";

    exportGridCsv(this.grid()!.api!, { fileName, columnKeys, skipColumnGroupHeaders: true });
  }

  onColumnRowGroupChanged(event: ColumnRowGroupChangedEvent<ProductAnalysisGridData>) {
    this.isGroupByParent.set(!!(event.column?.isRowGroupActive() && event.column.getColId() === PARENT_ASIN_COL_NAME));
  }

  toggleParentAsin() {
    this.isGroupByParent.update((v) => !v);
    this.grid()!.api.setRowGroupColumns(this.isGroupByParent() ? [PARENT_ASIN_COL_NAME] : []);
  }

  restoreDefaultColumns() {
    restoreDefaultColumns(this.localStorageKeyGrid(), this.grid()!.api, false);
  }

  private getDetailAsinMetrics(accountType: AccountType | undefined) {
    return accountType == AccountType.VENDOR ? this.DETAIL_ASIN_METRICS_VENDOR : this.DETAIL_ASIN_METRICS_SELLER;
  }

  private openAsinChartModal(node: IRowNode<ProductAnalysisGridData>) {
    const modalOption: ModalOptions<ChartModalInputData<AdStats>> = {
      modalTitle:
        node.isRowPinned() || node.data?.asin === undefined
          ? this.translocoService.translate("sales-advertising.total_stats")
          : this.translocoService.translate("sales-advertising.asin_stats", { asin: node.data.asin }),
      data: {
        locale: this.locale(),
        currency: this.currency(),
        dataSetConfiguration: {
          aggregationFunction: [mergeAllStats],
          metricsOnSameScale: DefaultAdStatsMetricsWithSameScale,
        },
        metrics: this.detailAsinMetrics(),
        selectedMetrics: this.selectedMetrics(),
        localStorageKey: this.localStorageKey,
        chartData$: this.getModalData(node),
      },
    };
    this.modalService.openModal<ChartModalInputData<AdStats>, void>(ChartModalComponent, modalOption);
  }

  private getModalData(node: IRowNode<ProductAnalysisGridData>): Observable<ChartData<AdStats>> {
    const targetedNodes = getFilteredLeafNodes(node, this.grid()!.api);
    const asins: string[] = [];
    for (const n of targetedNodes) {
      if (n.data?.asin) {
        asins.push(n.data.asin);
      }
    }
    const accountMarketplace = this.accountMarketplace();
    const currency = this.currency();
    return combineLatest([
      this.userSelectionService.dateRange$.pipe(
        switchMap((dateRange) =>
          this.statsService.getDailyProductStats(
            accountMarketplace.accountId,
            accountMarketplace.marketplace,
            currency,
            dateRange[0],
            dateRange[1],
            accountMarketplace.useSourcingMetrics ?? false,
            accountMarketplace.dspAdvertiserId,
            asins,
          ),
        ),
      ),
      this.userSelectionService.periodComparison$.pipe(
        switchMap((comparison) => {
          if (comparison?.period) {
            return this.statsService.getDailyProductStats(
              accountMarketplace.accountId,
              accountMarketplace.marketplace,
              currency,
              comparison.period[0],
              comparison.period[1],
              accountMarketplace.useSourcingMetrics ?? false,
              accountMarketplace.dspAdvertiserId,
              asins,
            );
          } else {
            return of([]);
          }
        }),
      ),
    ]).pipe(
      map(([stats, previousStats]) => {
        const adTypes = accountMarketplace.dspAdvertiserId ? this.selectedAdTypes() : [AdType.SponsoredAds];
        const data = convertToAdStats(stats, adTypes);
        const previousData = convertToAdStats(previousStats, adTypes);
        const totalData = data.reduce(
          (acc, curr) => mergeAllStats(acc as OrderStats & AdStats, curr as OrderStats & AdStats),
          {} as OrderStats & AdStats,
        );
        const totalPreviousData = previousData.reduce(
          (acc, curr) => mergeAllStats(acc as OrderStats & AdStats, curr as OrderStats & AdStats),
          {} as OrderStats & AdStats,
        );
        const chartData: ChartData<AdStats> = {
          data,
          previousData,
          totalData,
          totalPreviousData,
        };
        return chartData;
      }),
      shareReplay(1),
    );
  }
}
